import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertCircle, ChevronDown, Loader2, Plus } from 'lucide-react';
import { api } from '../utils/api';
import { useIsMobile } from '../utils/useIsMobile';
import { streamChatMessage } from '../components/chat/chatStream';
import OrganicShell from '../components/organic/OrganicShell';
import ThreadList from '../components/chat/ThreadList';
import MessageBubble from '../components/chat/MessageBubble';
import Composer from '../components/chat/Composer';
import EmptyState from '../components/chat/EmptyState';
import FollowUps from '../components/chat/FollowUps';
import '../components/chat/Chat.css';

// Full-page chat surface on the Organic shell. Three columns: the
// shared 76px icon rail, the thread rail, and the main column whose
// message list is the only thing that scrolls.
//
// On a phone there is no room for the 264px thread rail, so it moves
// behind the title bar: the conversation's name is a dropdown, and the
// same list opens under it.

const STROKE = 2.75;

function Chat() {
  const isMobile = useIsMobile();
  const [threadMenuOpen, setThreadMenuOpen] = useState(false);
  // ?thread=<id> deep-links a specific conversation — the Movers Ask
  // panel uses it to hand its thread over to the full-page chat.
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedThreadId = Number(searchParams.get('thread')) || null;

  const [threads, setThreads] = useState([]);
  const [threadsLoading, setThreadsLoading] = useState(true);
  const [activeThreadId, setActiveThreadId] = useState(requestedThreadId);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState(null);
  const [liveEvents, setLiveEvents] = useState([]);
  const [followUps, setFollowUps] = useState([]);
  const [thinkingPhase, setThinkingPhase] = useState(null);

  const messageListRef = useRef(null);
  const abortRef = useRef(null);
  const composerRef = useRef(null);
  // Mirror of `activeThreadId` for use inside streaming callbacks —
  // the closure over state would go stale if the user switches
  // threads while a reply is streaming.
  const activeThreadIdRef = useRef(null);
  useEffect(() => { activeThreadIdRef.current = activeThreadId; }, [activeThreadId]);

  // Scroll the message list to the bottom whenever a new message
  // appears or the streaming assistant grows. We only auto-scroll if
  // the user is already near the bottom — otherwise we'd yank them
  // out of a scroll-back.
  const scrollToBottomIfNear = useCallback(() => {
    const el = messageListRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 200;
    if (nearBottom) el.scrollTop = el.scrollHeight;
  }, []);

  // Load thread list on mount.
  useEffect(() => {
    let cancelled = false;
    setThreadsLoading(true);
    api.listChatThreads()
      .then(data => {
        if (cancelled) return;
        const list = data.threads || [];
        setThreads(list);
        // A deep-linked thread wins over "most recent" — but only if it
        // is still there, so a stale link falls back instead of showing
        // an empty pane. The param is consumed once selected so a later
        // thread switch isn't undone by a reload.
        const requested = requestedThreadId
          && list.some(t => t.id === requestedThreadId)
          ? requestedThreadId
          : null;
        if (requested) {
          setActiveThreadId(requested);
          setSearchParams({}, { replace: true });
        } else if (list.length && !activeThreadId) {
          setActiveThreadId(list[0].id);
        } else if (requestedThreadId && !requested) {
          setSearchParams({}, { replace: true });
          if (list.length) setActiveThreadId(list[0].id);
        }
      })
      .catch(err => !cancelled && setError(err.message))
      .finally(() => !cancelled && setThreadsLoading(false));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load messages whenever the active thread changes.
  useEffect(() => {
    if (!activeThreadId) {
      setMessages([]);
      setFollowUps([]);
      return;
    }
    let cancelled = false;
    setMessagesLoading(true);
    setFollowUps([]);
    api.getChatThread(activeThreadId)
      .then(data => {
        if (cancelled) return;
        const msgs = data.messages || [];
        setMessages(msgs);
        // If the last assistant message persisted follow-ups, restore
        // them so the user can resume the conversation with one click.
        const lastAssistant = [...msgs].reverse().find(m => m.role === 'assistant');
        if (lastAssistant && lastAssistant.follow_ups) {
          setFollowUps(lastAssistant.follow_ups);
        }
      })
      .catch(err => !cancelled && setError(err.message))
      .finally(() => !cancelled && setMessagesLoading(false));
    return () => { cancelled = true; };
  }, [activeThreadId]);

  // Bottom-scroll on relevant changes.
  useEffect(() => {
    scrollToBottomIfNear();
  }, [messages, streaming, scrollToBottomIfNear]);

  // Switching threads (or starting a new one) while a reply is
  // streaming aborts the stream — the backend persists the partial
  // turn, and without the abort the incoming tokens would be spliced
  // into whatever thread is on screen.
  const handleSelectThread = useCallback((id) => {
    if (abortRef.current && id !== activeThreadIdRef.current) {
      abortRef.current.abort();
    }
    setActiveThreadId(id);
  }, []);

  const handleNewThread = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    try {
      const t = await api.createChatThread();
      setThreads(prev => [t, ...prev]);
      setActiveThreadId(t.id);
      setMessages([]);
      setFollowUps([]);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  const handleDeleteThread = useCallback(async (id) => {
    if (!window.confirm('Delete this conversation?')) return;
    try {
      await api.deleteChatThread(id);
      setThreads(prev => prev.filter(t => t.id !== id));
      if (activeThreadId === id) {
        setActiveThreadId(null);
        setMessages([]);
      }
    } catch (err) {
      setError(err.message);
    }
  }, [activeThreadId]);

  const ensureThread = useCallback(async () => {
    if (activeThreadId) return activeThreadId;
    const t = await api.createChatThread();
    setThreads(prev => [t, ...prev]);
    setActiveThreadId(t.id);
    return t.id;
  }, [activeThreadId]);

  const handleSend = useCallback(async (content) => {
    setError(null);
    setFollowUps([]);

    let threadId;
    try {
      threadId = await ensureThread();
    } catch (err) {
      setError(err.message);
      // The composer cleared on submit — give the text back so the
      // user can retry without retyping.
      if (composerRef.current) composerRef.current.restore(content);
      return;
    }

    const optimisticUser = {
      role: 'user',
      content_md: content,
      created_at: new Date().toISOString(),
      _optimistic: true,
    };
    const optimisticAssistant = {
      role: 'assistant',
      content_md: '',
      streaming: true,
      tool_calls: null,
      tool_results: null,
      created_at: new Date().toISOString(),
      _optimistic: true,
    };
    setMessages(prev => [...prev, optimisticUser, optimisticAssistant]);
    setStreaming(true);
    setLiveEvents([]);
    setThinkingPhase('starting');

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      await streamChatMessage({
        threadId,
        content,
        signal: controller.signal,
        onEvent: (evt) => {
          // Thread-title updates target the rail, not the message
          // list — apply them regardless of which thread is open.
          if (evt.type === 'thread_titled') {
            setThreads(prev => prev.map(t => t.id === threadId ? { ...t, title: evt.title } : t));
            return;
          }
          // Everything else mutates the visible message list; drop it
          // if the user has switched away from this stream's thread.
          if (activeThreadIdRef.current !== threadId) return;
          if (evt.type === 'token') {
            setMessages(prev => {
              const next = [...prev];
              const last = next[next.length - 1];
              if (last && last.role === 'assistant') {
                next[next.length - 1] = {
                  ...last,
                  content_md: (last.content_md || '') + evt.text,
                };
              }
              return next;
            });
          } else if (evt.type === 'thinking') {
            setThinkingPhase(evt.detail || evt.phase || 'thinking');
          } else if (evt.type === 'tool_call_started' || evt.type === 'tool_call_finished') {
            setLiveEvents(prev => [...prev, evt]);
            if (evt.type === 'tool_call_started') {
              setThinkingPhase(`Running ${evt.name}…`);
            } else {
              setThinkingPhase(`Finished ${evt.name} (${evt.result_summary || 'ok'})`);
            }
          } else if (evt.type === 'report') {
            // Live report event — surface the wider bubble immediately
            // even before the assistant's wrap-up text finishes
            // streaming, so charts aren't held back by token latency.
            setMessages(prev => {
              const next = [...prev];
              const last = next[next.length - 1];
              if (last && last.role === 'assistant') {
                next[next.length - 1] = { ...last, report: evt.report };
              }
              return next;
            });
          } else if (evt.type === 'final') {
            setMessages(prev => {
              const next = [...prev];
              const last = next[next.length - 1];
              if (last && last.role === 'assistant') {
                next[next.length - 1] = {
                  ...last,
                  content_md: evt.content_md || last.content_md || '',
                  tool_calls: evt.tool_calls || [],
                  tool_results: evt.tool_results || [],
                  report: evt.report || last.report || null,
                  streaming: false,
                  _optimistic: false,
                };
              }
              return next;
            });
          } else if (evt.type === 'follow_ups') {
            setFollowUps(evt.suggestions || []);
          } else if (evt.type === 'error') {
            setError(evt.message || 'Chat error.');
          } else if (evt.type === 'done') {
            // No-op — final + follow_ups already updated state.
          }
        },
      });
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message || 'Stream failed.');
        // Restore the draft so a failed send doesn't eat the message.
        if (composerRef.current) composerRef.current.restore(content);
      }
    } finally {
      setStreaming(false);
      setThinkingPhase(null);
      abortRef.current = null;
      // If we're still looking at this stream's thread, make sure the
      // last assistant bubble isn't stuck in its streaming state
      // (e.g. after Stop, when no `final` event arrives).
      if (activeThreadIdRef.current === threadId) {
        setMessages(prev => prev.map((m, i) => (
          i === prev.length - 1 && m.role === 'assistant' && m.streaming
            ? { ...m, streaming: false }
            : m
        )));
      }
      // Bump the thread to the top of the rail.
      setThreads(prev => {
        const t = prev.find(x => x.id === threadId);
        if (!t) return prev;
        const others = prev.filter(x => x.id !== threadId);
        return [{ ...t, updated_at: new Date().toISOString() }, ...others];
      });
    }
  }, [ensureThread]);

  // Live assistant message gets the in-progress liveEvents so the
  // trace card renders while tools are still running.
  const messagesWithLive = useMemo(() => {
    if (!streaming) return messages;
    return messages.map((m, idx) =>
      idx === messages.length - 1 && m.role === 'assistant'
        ? { ...m, _liveEvents: liveEvents }
        : m,
    );
  }, [messages, liveEvents, streaming]);

  const stop = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
  }, []);

  const activeThread = threads.find((t) => t.id === activeThreadId);

  // Phone title bar: the thread name doubles as the rail's trigger, and
  // "new conversation" keeps a 44px target of its own.
  const mobileTopBar = (
    <div className="o-topbar chat-topbar">
      <button
        type="button"
        className="chat-topbar-title"
        aria-expanded={threadMenuOpen}
        aria-label="Switch conversation"
        onClick={() => setThreadMenuOpen((v) => !v)}
      >
        <span className="chat-topbar-name">
          {threadsLoading ? 'Loading…' : (activeThread?.title || 'New conversation')}
        </span>
        <ChevronDown size={16} strokeWidth={STROKE} />
      </button>
      <button
        type="button"
        className="btn btn-secondary btn-icon chat-topbar-new"
        aria-label="New conversation"
        title="New conversation"
        onClick={() => { setThreadMenuOpen(false); handleNewThread(); }}
      >
        <Plus size={18} strokeWidth={STROKE} />
      </button>
      {threadMenuOpen && (
        <>
          <div className="chat-thread-scrim" onClick={() => setThreadMenuOpen(false)} />
          <div className="chat-thread-dropdown">
            <ThreadList
              threads={threads}
              loading={threadsLoading}
              activeId={activeThreadId}
              onSelect={(id) => { handleSelectThread(id); setThreadMenuOpen(false); }}
              onNew={() => { setThreadMenuOpen(false); handleNewThread(); }}
              onDelete={handleDeleteThread}
            />
          </div>
        </>
      )}
    </div>
  );

  return (
    <OrganicShell
      active="chat"
      contentStyle={{ padding: 0, maxWidth: 'none', gap: 0, height: isMobile ? 'auto' : '100vh', overflow: 'hidden' }}
      mobileTopBar={mobileTopBar}
    >
      <div className="chat-page">
        {!isMobile && (
          <ThreadList
            threads={threads}
            loading={threadsLoading}
            activeId={activeThreadId}
            onSelect={handleSelectThread}
            onNew={handleNewThread}
            onDelete={handleDeleteThread}
          />
        )}

        <main className="chat-main">
          {error && (
            <div className="chat-error">
              <AlertCircle size={16} strokeWidth={STROKE} />
              <span>{error}</span>
              <button type="button" onClick={() => setError(null)} className="chat-error-close">×</button>
            </div>
          )}

          <div className="chat-messages" ref={messageListRef}>
            <div className="chat-messages-inner">
              {messagesLoading ? (
                <div className="chat-loading">
                  <Loader2 size={20} strokeWidth={STROKE} className="spin" />
                  <span>Loading conversation…</span>
                </div>
              ) : messagesWithLive.length === 0 ? (
                <EmptyState onPick={handleSend} />
              ) : (
                <>
                  {messagesWithLive.map((m, idx) => (
                    <MessageBubble
                      key={m.id || `optimistic-${idx}`}
                      message={m}
                      liveEvents={m._liveEvents}
                    />
                  ))}
                  {streaming && thinkingPhase && (
                    <div className="chat-thinking-line">
                      <Loader2 size={14} strokeWidth={STROKE} className="spin" />
                      <span>{thinkingPhase}</span>
                    </div>
                  )}
                  <FollowUps
                    suggestions={!streaming ? followUps : []}
                    onPick={(s) => handleSend(s)}
                  />
                </>
              )}
            </div>
          </div>

          <div className="chat-composer-region">
            <Composer
              ref={composerRef}
              onSend={handleSend}
              disabled={streaming}
              streaming={streaming}
              onStop={stop}
              placeholder={
                streaming
                  ? 'Streaming…'
                  : (activeThreadId ? 'Ask a follow-up, or /command…' : 'Ask about a brand, or pick a starter to begin…')
              }
            />
          </div>
        </main>
      </div>
    </OrganicShell>
  );
}

export default Chat;
